import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldProse, NumberedSection, PanelCard, RegaraNoteList } from './DossierPrimitives';
import {
  fetchConformityRoute,
  type ConformityRoute,
  type NbConsultation,
  type ConformityRouteResult,
} from '@/features/dossier/api';

/**
 * EU MDR Conformity Assessment Route panel.
 *
 * Split into:
 *   - `ConformityRouteRenderer` — presentational, accepts a
 *     `ConformityRouteResult` as a prop. Unit-testable without fetch.
 *   - `ConformityRoutePanel` — container; fetches via `fetchConformityRoute`
 *     and delegates rendering to `ConformityRouteRenderer`.
 *
 * Renders:
 *   §1 Primary Route (basis + TD-assessment-basis + exemptions if present)
 *   §2 Alternative Route (same shape)
 *   §3 Notified Body Consultations (name + Applies when + description)
 *   §4 Conformity Route Output Chain (ordered list)
 *   Notes (when present)
 *   Download PDF / DOCX links targeting GET .../conformity-route?device_class=…&format=pdf|docx
 */

// ── Re-export types so existing importers (e.g. tests) keep resolving ─────────

export type { ConformityRoute, NbConsultation, ConformityRouteResult };

// ── Presentational renderer ───────────────────────────────────────────────────

export function ConformityRouteRenderer({
  result,
  productId,
  deviceClass,
  hideDownload = false,
}: {
  result: ConformityRouteResult;
  productId: number;
  deviceClass: string;
  hideDownload?: boolean;
}) {
  const baseUrl = `/api/products/${productId}/conformity-route?device_class=${encodeURIComponent(deviceClass)}`;

  return (
    <div className="space-y-4">
      <ConformityCoverBlock deviceClass={result.device_class} />

      <Section1PrimaryRoute route={result.primary_route} />
      <Section2AlternativeRoute route={result.alternative_route} />

      <Section3NbConsultations consultations={result.nb_consultations} />

      <Section4OutputChain steps={result.output_chain} />

      <RegaraNoteList notes={result.notes} />

      {!hideDownload && <DownloadBar baseUrl={baseUrl} />}
    </div>
  );
}

// ── Cover block ───────────────────────────────────────────────────────────────

function ConformityCoverBlock({ deviceClass }: { deviceClass: string }) {
  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU MDR Conformity Assessment Route
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/745 — Article 52
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
            Device class (EU MDR)
          </div>
          <div className="text-[13px] text-navy font-semibold">{deviceClass}</div>
        </div>
        <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
            Reference
          </div>
          <div className="text-[13px] text-navy font-semibold">
            Regulation (EU) 2017/745 — Article 52 / Annex IX–XI
          </div>
        </div>
      </div>
    </PanelCard>
  );
}

// ── Route section (shared shape for primary + alternative) ────────────────────

function RouteSection({
  num,
  title,
  route,
}: {
  num: number;
  title: string;
  route: ConformityRoute;
}) {
  return (
    <NumberedSection prefix="Section" num={num} title={title}>
      <FieldProse label="Legal basis:" body={route.basis} fallbackHint="Conformity route basis" />
      <FieldProse
        label="TD assessment basis:"
        body={route.td_assessment_basis}
        fallbackHint="Technical documentation assessment basis"
      />
      {route.exemptions && route.exemptions.length > 0 && (
        <div className="my-3">
          <div className="font-semibold text-ink/80 text-[13px] mb-1">Exemptions:</div>
          <ul className="list-disc pl-5 m-0 space-y-1 text-[13.5px]">
            {route.exemptions.map((ex, i) => (
              <li key={i}>{ex}</li>
            ))}
          </ul>
        </div>
      )}
    </NumberedSection>
  );
}

function Section1PrimaryRoute({ route }: { route: ConformityRoute }) {
  return (
    <RouteSection num={1} title="Primary Conformity Assessment Route" route={route} />
  );
}

function Section2AlternativeRoute({ route }: { route: ConformityRoute }) {
  return (
    <RouteSection num={2} title="Alternative Conformity Assessment Route" route={route} />
  );
}

// ── §3 NB consultations ───────────────────────────────────────────────────────

function Section3NbConsultations({
  consultations,
}: {
  consultations: NbConsultation[];
}) {
  return (
    <NumberedSection prefix="Section" num={3} title="Notified Body Consultations">
      {consultations.length === 0 ? (
        <p className="text-[13.5px] text-muted italic m-0">
          No Notified Body consultations applicable.
        </p>
      ) : (
        <div className="space-y-4">
          {consultations.map((c, i) => (
            <div
              key={i}
              className="border-l-2 border-line pl-4 py-1"
            >
              <div className="font-semibold text-navy text-[14px] mb-1">{c.name}</div>
              <div className="text-[12.5px] text-muted mb-1.5">
                <span className="font-semibold">Applies when:</span> {c.applies_when}
              </div>
              <div className="text-[13.5px] leading-[1.6] text-ink">{c.description}</div>
            </div>
          ))}
        </div>
      )}
    </NumberedSection>
  );
}

// ── §4 Output chain ───────────────────────────────────────────────────────────

function Section4OutputChain({ steps }: { steps: string[] }) {
  return (
    <NumberedSection prefix="Section" num={4} title="Output Chain">
      {steps.length === 0 ? (
        <p className="text-[13.5px] text-muted italic m-0">No output steps specified.</p>
      ) : (
        <ol className="list-decimal pl-5 m-0 space-y-2 text-[13.5px]">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}
    </NumberedSection>
  );
}

// ── Download bar ──────────────────────────────────────────────────────────────

function DownloadBar({ baseUrl }: { baseUrl: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="brand" size="sm" asChild>
        <a
          href={`${baseUrl}&format=pdf`}
          target="_blank"
          rel="noopener"
          title="Download Conformity Route as PDF"
        >
          <Download className="size-3.5" /> Download PDF
        </a>
      </Button>
    </div>
  );
}

// ── Container (fetches data) ──────────────────────────────────────────────────

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; result: ConformityRouteResult };

export function ConformityRoutePanel({
  productId,
  deviceClass,
}: {
  productId: number;
  deviceClass: string;
}) {
  const [state, setState] = useState<FetchState>({ status: 'idle' });

  const load = async () => {
    setState({ status: 'loading' });
    try {
      const result = await fetchConformityRoute(productId, deviceClass);
      setState({ status: 'ok', result });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to load conformity route.',
      });
    }
  };

  if (state.status === 'idle') {
    return (
      <PanelCard>
        <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-3">
          EU MDR Conformity Assessment Route
        </div>
        <p className="text-[13px] text-muted m-0 mb-4">
          Generate a conformity assessment route summary for this device class.
        </p>
        <Button variant="brand" size="sm" onClick={() => void load()}>
          Load Conformity Route
        </Button>
      </PanelCard>
    );
  }

  if (state.status === 'loading') {
    return (
      <PanelCard>
        <p className="text-[13px] text-muted italic m-0">
          Loading conformity route…
        </p>
      </PanelCard>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
        <div className="font-semibold text-state-error-text text-[14px] mb-1">
          Could not load conformity route
        </div>
        <p className="text-[13px] text-state-error-text m-0 mb-3">{state.message}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ConformityRouteRenderer
      result={state.result}
      productId={productId}
      deviceClass={deviceClass}
    />
  );
}
